import { normalizeStatisticsHours } from "./statisticsHoursFilter";
import type { StatisticsTab } from "../types";

export type StatisticsTimePeriod =
  | "all"
  | "7"
  | "30"
  | "3months"
  | "yesterday"
  | "today"
  | "custom";

/** A header panel a link opens once: the settings and notifications sheets, the export center or the AI report. */
export const STATISTICS_PANELS = ["settings", "notifications", "export", "report"] as const;
export type StatisticsPanel = (typeof STATISTICS_PANELS)[number];

export interface StatisticsSearchState {
  tab: StatisticsTab;
  period: StatisticsTimePeriod;
  from: string | null;
  to: string | null;
  experiment: string | null;
  hours?: string;
  panel?: StatisticsPanel;
}

const VALID_TABS = new Set<StatisticsTab>(["performance", "topics", "livechat"]);

const VALID_PERIODS = new Set<StatisticsTimePeriod>([
  "all",
  "7",
  "30",
  "3months",
  "yesterday",
  "today",
  "custom",
]);

/** Map legacy tab values from bookmarks to the current tabs. */
function normalizeTab(raw: unknown): StatisticsTab {
  const value = typeof raw === "string" ? raw : "performance";
  if (VALID_TABS.has(value as StatisticsTab)) return value as StatisticsTab;
  if (
    value === "overview" ||
    value === "satisfaction" ||
    value === "conversations" ||
    value === "ratings" ||
    value === "export" ||
    value === "settings" ||
    value === "notifikationer"
  ) {
    return "performance";
  }
  return "performance";
}

function normalizePeriod(raw: unknown): StatisticsTimePeriod {
  // The router parses query values as JSON, so a link's `?period=7` arrives as the number 7.
  const value = typeof raw === "string" || typeof raw === "number" ? String(raw) : "30";
  return VALID_PERIODS.has(value as StatisticsTimePeriod) ? (value as StatisticsTimePeriod) : "30";
}

export function parseStatisticsSearch(search: Record<string, unknown>): StatisticsSearchState {
  return {
    tab: normalizeTab(search.tab),
    period: normalizePeriod(search.period),
    from: typeof search.from === "string" && search.from ? search.from : null,
    to: typeof search.to === "string" && search.to ? search.to : null,
    experiment:
      typeof search.experiment === "string" && search.experiment.trim()
        ? search.experiment.trim()
        : null,
    ...(normalizeStatisticsHours(search.hours)
      ? { hours: normalizeStatisticsHours(search.hours) }
      : {}),
    ...((STATISTICS_PANELS as readonly unknown[]).includes(search.panel)
      ? { panel: search.panel as StatisticsPanel }
      : {}),
  };
}

export function serializeStatisticsSearch(state: StatisticsSearchState): StatisticsSearchState {
  return {
    tab: state.tab,
    period: state.period,
    from: state.period === "custom" ? state.from : null,
    to: state.period === "custom" ? state.to : null,
    experiment: state.experiment,
    ...(state.hours ? { hours: state.hours } : {}),
  };
}

export function parseCustomDate(value: string | null): Date | null {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function formatCustomDate(date: Date | null): string | null {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
